import { Bar, FrontDesk, Housekeeping, Kiosk, Room, Staff } from '@rocksoft/locations-sdk';

import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { CategoryType, Device } from '@rocksoft/shared';
import { Category } from '../router/category';
import { CategoryTypeToEntity } from '../shared/types';
import * as fromCategory from './category.reducer';
import {
  CategoriesUnion,
  categoryRequestKey,
  CategoryEntityByType,
  CategoryState,
  isCategoryEntityType,
} from './category.state';

// ============================================================================
// STATE SELECTORS
// ============================================================================

/**
 * Base selector for the category feature state.
 * Provides access to the entire category store state.
 */
export const selectCategoryState = createFeatureSelector<CategoryState>('category');
/**
 * Selects all category IDs from the store.
 * Returns an array of category IDs in the format: "CategoryType-categoryId"
 */
export const selectCategoryIds = createSelector(selectCategoryState, fromCategory.selectCategoryIds);

/**
 * Selects all category entity configurations from the store.
 * Returns an array of all categories regardless of type.
 */
export const selectAllCategories = createSelector(selectCategoryState, fromCategory.selectAllCategories);

/**
 * Selects all category entities as a dictionary/map.
 * Key format: "CategoryType-categoryId", value: CategoryEntityConfiguration
 * Useful for quick lookups by ID.
 */
export const selectCategoryEntities = createSelector(selectCategoryState, fromCategory.selectCategoryEntities);

export const selectCategoryRequestStatuses = createSelector(
  selectCategoryState,
  (state) => state.requestStatuses ?? {}
);

export const selectCategoryRequestStatus = (categoryType: CategoryType, parentCategoryId?: string) =>
  createSelector(
    selectCategoryRequestStatuses,
    (statuses) => statuses[categoryRequestKey(categoryType, parentCategoryId)]
  );

// ============================================================================
// GENERIC SELECTORS
// ============================================================================

/**
 * Selects all category entity configurations filtered by a specific category type.
 * Returns an array of CategoryEntityConfiguration objects matching the given type.
 * @param categoryType - The type of category to filter (e.g., 'Bar', 'Room', 'Location')
 * @returns Memoized selector that returns CategoryEntityConfiguration[]
 */
export function selectAllCategoriesByType<T extends CategoryType>(
  categoryType: T
): MemoizedSelector<object, CategoryEntityByType<T>[]> {
  return createSelector(selectAllCategories, (categories: CategoriesUnion[]) =>
    categories
      ? categories.filter<CategoryEntityByType<T>>((category): category is CategoryEntityByType<T> =>
          isCategoryEntityType(category, categoryType)
        )
      : []
  );
}

export function selectAllCategoryEntitiesByType<T extends CategoryType>(
  categoryType: T
): MemoizedSelector<object, CategoryTypeToEntity[T][]> {
  return createSelector(selectAllCategoriesByType(categoryType), (categories: CategoryEntityByType<T>[]) =>
    categories
      .map((category) => category.entity)
      .filter((entity): entity is CategoryTypeToEntity[T] => entity !== undefined)
  );
}

/**
 * Selects a specific category by its ID and type.
 * @param categoryId - The ID of the category (not the categoryId)
 * @param categoryType - The type of category to find
 * @returns Memoized selector that returns the CategoryEntityConfiguration or undefined if not found
 */
export function selectCategory<T extends CategoryType>(
  categoryType: T,
  categoryId: string
): MemoizedSelector<object, CategoryEntityByType<T> | undefined> {
  return createSelector(selectAllCategoriesByType(categoryType), (categories: CategoryEntityByType<T>[]) =>
    categories ? categories.find((cat) => cat.categoryId === categoryId) : undefined
  );
}

/**
 * Returns wether or not the given category ID has children of the given category type.
 * @param categoryId - The ID of the category
 * @param categoryType - The type of category to find
 * @returns true if the category has children of the given type, false otherwise
 */
export function hasChildCategoriesOfCategoryType(
  categoryType: CategoryType,
  categoryId: string
): MemoizedSelector<object, boolean> {
  return createSelector(
    selectAllCategoriesByType(categoryType),
    (categories: CategoryEntityByType<CategoryType>[]) =>
      !!categories.find((cat) => cat.parent?.categoryId === categoryId)
  );
}

export function selectCategoryEntity<T extends CategoryType>(
  categoryType: T,
  categoryId: string
): MemoizedSelector<object, CategoryTypeToEntity[T] | undefined> {
  return createSelector(
    selectCategory(categoryType, categoryId),
    (category: CategoryEntityByType<T> | undefined) => category?.entity
  );
}

/**
 * Checks if a category exists in the store by its ID and type.
 * @param categoryId - The ID of the category to check
 * @param categoryType - The type of category to check
 * @returns Memoized selector that returns true if the category exists, false otherwise
 */
export const hasCategory = (categoryId: string, categoryType: CategoryType): MemoizedSelector<object, boolean> =>
  createSelector(selectCategory(categoryType, categoryId), (cat) => !!cat);

/**
 * Selects a category by its categoryId (works for Location and Room types only).
 * The categoryId is different from the regular ID and is used for external references.
 * @param categoryId - The unique identifier of the category
 * @returns Memoized selector that returns the CategoryEntityConfiguration or undefined if not found
 */
export function selectCategoryByCategoryId<T extends CategoryType>(
  categoryType: T,
  categoryId: string
): MemoizedSelector<object, CategoryEntityByType<T> | undefined> {
  return createSelector(selectAllCategoriesByType(categoryType), (categories: CategoryEntityByType<T>[]) => {
    return categories
      ? categories.find((cat: CategoriesUnion) => {
          if (cat.categoryType === 'Location') {
            return cat.entity?.categoryId === categoryId;
          }
          if (cat.categoryType === 'Room') {
            return cat.entity?.categoryId === categoryId;
          }
          return cat.categoryId === categoryId;
        })
      : undefined;
  });
}

/**
 * True when the device has at least one category assignment (room, location, etc.).
 * Use before calling selectCategoryByDevice(device) so store.select() never receives undefined.
 */
export function deviceHasCategoryId(device: Device): boolean {
  return !!(
    device?.roomCategoryId ||
    device?.frontDeskCategoryId ||
    device?.housekeepingCategoryId ||
    device?.kioskCategoryId ||
    device?.staffCategoryId ||
    device?.barCategoryId ||
    device?.locationCategoryId ||
    device?.brandCategoryId
  );
}

/**
 * Selects the most concrete (lowest level)category for a device.
 * @param device - The device to select the category for
 * @returns Memoized selector that returns the Category or undefined if not found
 */
export function selectCategoryByDevice(device: Device): MemoizedSelector<object, Category | undefined> {
  if (device.roomCategoryId) {
    return selectCategory('Room', device.roomCategoryId);
  }
  if (device.frontDeskCategoryId) {
    return selectCategory('FrontDesk', device.frontDeskCategoryId);
  }
  if (device.housekeepingCategoryId) {
    return selectCategory('Housekeeping', device.housekeepingCategoryId);
  }
  if (device.kioskCategoryId) {
    return selectCategory('Kiosk', device.kioskCategoryId);
  }
  if (device.staffCategoryId) {
    return selectCategory('Staff', device.staffCategoryId);
  }
  if (device.barCategoryId) {
    return selectCategory('Bar', device.barCategoryId);
  }
  if (device.locationCategoryId) {
    return selectCategory('Location', device.locationCategoryId);
  }
  if (device.brandCategoryId) {
    return selectCategory('Brand', device.brandCategoryId);
  }
  return createSelector(selectCategoryState, () => undefined as Category | undefined);
}

/**
 * Selects the category ID for a category of a specific type that belongs to a location.
 * Useful for finding location-specific categories like Bar, FrontDesk, etc. by location ID.
 * @param categoryType - The type of category to find (Room, Bar, FrontDesk, Housekeeping, Kiosk, Staff)
 * @param locationId - The location ID to filter by
 * @returns Memoized selector that returns the category ID string or undefined if not found
 */
export const selectCategoryIdByLocationId = (
  categoryType: string,
  locationId: string
): MemoizedSelector<object, string | undefined> =>
  createSelector(selectAllCategories, (categories: CategoriesUnion[]) => {
    return categories
      ? categories.find((cat: CategoriesUnion) => {
          if (cat.categoryType !== categoryType) {
            return false;
          }

          switch (categoryType) {
            case 'Room': {
              const room = cat.entity as Room;
              return room.locationId === locationId;
            }
            case 'Bar': {
              const bar = cat.entity as Bar;
              return bar.locationId === locationId;
            }
            case 'FrontDesk': {
              const frontDesk = cat.entity as FrontDesk;
              return frontDesk.locationId === locationId;
            }
            case 'Housekeeping': {
              const housekeeping = cat.entity as Housekeeping;
              return housekeeping.locationId === locationId;
            }
            case 'Kiosk': {
              const kiosk = cat.entity as Kiosk;
              return kiosk.locationId === locationId;
            }
            case 'Staff': {
              const staff = cat.entity as Staff;
              return staff.locationId === locationId;
            }
            default:
              return false;
          }
        })?.categoryId
      : undefined;
  });

// ============================================================================
// ROOM SELECTORS
// ============================================================================
// Note: We will step away from using these selectors in the future, so don't
// add new ones or build upon them.
// ============================================================================

/**
 * Selects a specific room by location ID and room ID.
 * Uses the composite key format: "locationId-roomId" to identify the room.
 * @param locationId - The location ID that contains the room
 * @param roomId - The room ID
 * @returns Memoized selector that returns the Room CategoryEntityConfiguration or undefined if not found
 */
export const selectRoom = (locationId: string, roomId: string): MemoizedSelector<object, Room | undefined> =>
  createSelector(selectRoomsByLocationId(locationId), (rooms) =>
    rooms ? rooms.find((x) => x.id === roomId) : undefined
  );

/**
 * Checks if a room exists in the store by location ID and room ID.
 * @param locationId - The location ID that contains the room
 * @param roomId - The room ID
 * @returns Memoized selector that returns true if the room exists, false otherwise
 */
export const hasRoom = (locationId: string, roomId: string) =>
  createSelector(selectRoom(locationId, roomId), (room) => !!room);

/**
 * Selects all rooms that belong to a specific location.
 * @param locationId - The location ID to filter rooms by
 * @returns Memoized selector that returns an array of Room CategoryEntityConfiguration objects
 */
export const selectRoomsByLocationId = (locationId: string): MemoizedSelector<object, Room[]> =>
  createSelector(selectAllCategoryEntitiesByType('Room'), (rooms: Room[]) =>
    rooms ? rooms.filter((x) => x.locationId === locationId) : []
  );
